/* eslint-disable react-hooks/exhaustive-deps */
import { GameDetail, GameResultSynthesis } from '@orientaction/types';
import { getFromLS, getRestartedResultId, isAdminVerify } from '@orientaction/utils';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { apiServiceNative } from '../../../../../utils/fetchNative';
import { getBeneficiaryResourcesBackPath } from '../../../Tests/shared/adminResultNav';
import { groupStepsBySuccessRank, unwrapHistory, unwrapSynthesis } from '../utils';

const buildResultPath = (resultId: string | number, search: string) => {
  const params = new URLSearchParams(search);
  const next = new URLSearchParams();
  if (params.get('from') === 'admin') {
    next.set('from', 'admin');
  }
  const userId = params.get('userId');
  if (userId) {
    next.set('userId', userId);
  }
  const query = next.toString();
  return query ? `/game/result/${resultId}?${query}` : `/game/result/${resultId}`;
};

export const useGameSynthesis = () => {
  const history = useHistory();
  const location = useLocation();
  const { idResult }: { idResult: string } = useParams();
  const currentUser = JSON.parse(getFromLS('userInfos') || '{}');
  const searchParams = new URLSearchParams(location.search);
  const fromAdmin = searchParams.get('from') === 'admin';
  const beneficiaryUserId = searchParams.get('userId') || '';
  const isStaffViewer = fromAdmin || isAdminVerify();
  const hideRetake = isStaffViewer;
  const resourcesBackPath =
    isStaffViewer && beneficiaryUserId
      ? getBeneficiaryResourcesBackPath(beneficiaryUserId)
      : isStaffViewer
      ? '/admin/users-resultats'
      : '/mes-ressources';

  const [synthesis, setSynthesis] = useState<GameResultSynthesis | null>(null);
  const [coverUrl, setCoverUrl] = useState('');
  const [historyItems, setHistoryItems] = useState<Array<{ id: number; createdAt: string }>>([]);
  const [beneficiaryName, setBeneficiaryName] = useState('—');
  const [loading, setLoading] = useState(true);
  const [restarting, setRestarting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!idResult) {
        history.replace(resourcesBackPath);
        return;
      }

      setLoading(true);

      try {
        const payload = await apiServiceNative.get(`/api/game-result-synthesis/${idResult}`);
        const data = unwrapSynthesis(payload);

        if (cancelled) {
          return;
        }

        if (!data || data.status !== 'completed' || !(Number(data.scoreMaximum) > 0)) {
          history.replace(resourcesBackPath);
          return;
        }

        setSynthesis(data);

        const [details, historyResponse, beneficiary] = await Promise.all([
          apiServiceNative.get(`/api/details-game/${data.idGame}`).catch(() => null),
          apiServiceNative
            .get(`/api/game-result-history?userId=${data.userId}&gameId=${data.idGame}`)
            .catch(() => []),
          beneficiaryUserId
            ? apiServiceNative.get(`/api/users/${beneficiaryUserId}`).catch(() => null)
            : Promise.resolve(null),
        ]);

        if (cancelled) {
          return;
        }

        const detailsGame = details as GameDetail | null;
        setCoverUrl(data.couverture || detailsGame?.couverture || '');
        const items = unwrapHistory(historyResponse).map(item => ({
          id: item.idResult,
          createdAt: item.completedAt || item.createdAt || item.startedAt || '',
        }));

        if (!items.some(item => item.id === data.idResult)) {
          items.push({
            id: data.idResult,
            createdAt: data.completedAt || data.startedAt || '',
          });
        }

        items.sort((a, b) => new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf());
        setHistoryItems(items);

        const beneficiarySource = (beneficiary as { data?: any })?.data || beneficiary;
        const firstName =
          beneficiarySource?.firstname ||
          beneficiarySource?.firstName ||
          currentUser?.firstname ||
          currentUser?.firstName ||
          '';
        const lastName =
          beneficiarySource?.lastname ||
          beneficiarySource?.lastName ||
          currentUser?.lastname ||
          currentUser?.lastName ||
          '';
        setBeneficiaryName(`${firstName} ${lastName}`.trim() || '—');
      } catch (error) {
        if (!cancelled) {
          history.replace(resourcesBackPath);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [idResult, history, resourcesBackPath, beneficiaryUserId]);

  const groups = useMemo(
    () => groupStepsBySuccessRank(synthesis?.steps || []),
    [synthesis?.steps],
  );

  const totalSteps = synthesis?.steps.length || 0;

  const recapRanks = useMemo(() => {
    const byRank = new Map(groups.map(group => [group.rank, group]));
    return [1, 2, 3]
      .map(rank => {
        const group = byRank.get(rank);
        return {
          rank,
          stepCount: group?.steps.length || 0,
          points: group?.steps.reduce((sum, step) => sum + (step.points || 0), 0) || 0,
        };
      })
      .filter(item => item.stepCount > 0);
  }, [groups]);

  const restartGame = async () => {
    if (!synthesis || restarting || hideRetake) {
      return;
    }

    setRestarting(true);

    try {
      const response = await apiServiceNative.put(
        `/api/restart-game-result/${synthesis.idResult}`,
        {},
      );
      const newIdResult = getRestartedResultId(response);
      if (!newIdResult) {
        return;
      }
      history.push(`/game/view/${synthesis.idGame}/${newIdResult}`);
    } finally {
      setRestarting(false);
    }
  };

  const selectHistory = (id: string | number) => {
    if (Number(id) === Number(idResult)) {
      return;
    }
    history.push(buildResultPath(id, location.search));
  };

  const goBack = () => {
    const ownerUserId = beneficiaryUserId || synthesis?.userId;
    if (isStaffViewer) {
      history.push(getBeneficiaryResourcesBackPath(ownerUserId));
      return;
    }
    history.push('/mes-ressources');
  };

  return {
    beneficiaryName,
    coverUrl,
    fromAdmin,
    goBack,
    groups,
    hideRetake,
    historyItems,
    idResult: Number(idResult),
    loading,
    recapRanks,
    resourcesBackPath,
    restartGame,
    restarting,
    selectHistory,
    synthesis,
    totalSteps,
  };
};
